/**
 * /settings/support/email — global Google Workspace SMTP configuration.
 *
 * The password is write-only: the GET response only carries `has_password`,
 * we render an opaque placeholder when one is set, and clearing the field
 * sends an empty string (which deletes the encrypted blob server-side).
 */
import { useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Mail, Send, ShieldCheck, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { UnsavedChangesGuard } from '@/components/system/UnsavedChangesGuard';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { SaveBar, pluralizeChanges } from '@/components/ui/SaveBar';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useEmailSettings,
  usePatchEmailSettings,
  useTestEmailSettings,
  type CrmEmailSettings,
  type CrmEmailSettingsPayload,
} from '@/api/bi/crmSettings';
import { useDirtyForm } from '@/hooks/useDirtyForm';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

interface FormState extends Record<string, unknown> {
  host: string;
  port: string;
  use_tls: boolean;
  use_ssl: boolean;
  username: string;
  from_email: string;
  from_name: string;
  is_enabled: boolean;
  password: string;
  password_dirty: boolean;
}

function fromServer(server: CrmEmailSettings | undefined): FormState {
  return {
    host: server?.host ?? 'smtp.gmail.com',
    port: String(server?.port ?? 587),
    use_tls: server?.use_tls ?? true,
    use_ssl: server?.use_ssl ?? false,
    username: server?.username ?? '',
    from_email: server?.from_email ?? '',
    from_name: server?.from_name ?? 'Fasto Support',
    is_enabled: server?.is_enabled ?? false,
    password: '',
    password_dirty: false,
  };
}

export function CrmEmailSettingsPage() {
  const settingsQuery = useEmailSettings();
  const patch = usePatchEmailSettings();
  const test = useTestEmailSettings();
  const [testEmail, setTestEmail] = useState('');

  const serverForm = useMemo(
    () => (settingsQuery.data ? fromServer(settingsQuery.data) : undefined),
    [settingsQuery.data],
  );

  // `password_dirty` is bookkeeping for the write-only password, not a field the
  // user edits, so it must not count towards the change tally on its own.
  const form = useDirtyForm<FormState>({ data: serverForm, ignore: ['password_dirty'] });

  async function handleSave() {
    if (!form) return;
    const value = form.value;
    const portInt = Number(value.port);
    if (!Number.isFinite(portInt) || portInt <= 0 || portInt > 65535) {
      notify.error('Port must be a valid TCP port number.');
      return;
    }
    const payload: CrmEmailSettingsPayload = {
      host: value.host.trim() || 'smtp.gmail.com',
      port: portInt,
      use_tls: value.use_tls,
      use_ssl: value.use_ssl,
      username: value.username.trim(),
      from_email: value.from_email.trim(),
      from_name: value.from_name.trim(),
      is_enabled: value.is_enabled,
    };
    if (value.password_dirty) {
      payload.password = value.password;
    }
    try {
      await patch.mutateAsync(payload);
      notify.success('Email settings saved.');
      // Commit rather than wait for the refetch, so the save bar disappears the
      // moment the write lands instead of one network round-trip later.
      form.commit({ ...value, password: '', password_dirty: false });
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save email settings.'));
    }
  }

  async function handleTest() {
    if (!form) return;
    const value = form.value;
    if (!testEmail.trim() || !testEmail.includes('@')) {
      notify.error('Enter a recipient email to send a test message.');
      return;
    }
    // Test-send now mirrors the production resolver: every field we send
    // becomes a per-call override on top of the saved singleton, including
    // `is_enabled`. That means a passing test guarantees real CRM
    // notifications will go through using the same merged config.
    try {
      const result = await test.mutateAsync({
        to_email: testEmail.trim(),
        host: value.host || undefined,
        port: value.port ? Number(value.port) : undefined,
        use_tls: value.use_tls,
        use_ssl: value.use_ssl,
        username: value.username || undefined,
        from_email: value.from_email || undefined,
        from_name: value.from_name || undefined,
        is_enabled: value.is_enabled,
        password: value.password_dirty ? value.password : undefined,
      });
      if (result.ok) {
        notify.success(`Test email sent to ${testEmail.trim()}.`);
      } else {
        notify.error(`SMTP rejected the test: ${result.error ?? 'unknown error'}`);
      }
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not test email config.'));
    }
  }

  const enabled = form?.value.is_enabled ?? false;

  return (
    <div className={form?.isDirty ? 'pb-24' : undefined}>
      <UnsavedChangesGuard
        when={!!form?.isDirty}
        description="The SMTP configuration has unsaved changes. Outbound CRM email keeps using the saved settings until you save."
      />

      <PageHeader
        eyebrow="Support CRM"
        title="Email (SMTP)"
        description="Single Google Workspace SMTP account used for every outbound CRM notification. Credentials are encrypted at rest."
        actions={
          form ? (
            <Badge tone={enabled ? 'success' : 'neutral'}>
              <ShieldCheck className="mr-1 h-3 w-3" /> {enabled ? 'enabled' : 'disabled'}
            </Badge>
          ) : null
        }
      />

      {settingsQuery.isError ? (
        <ErrorState
          title="Could not load SMTP settings"
          description={apiErrorMessage(settingsQuery.error, 'The settings endpoint did not respond.')}
          onRetry={() => void settingsQuery.refetch()}
        />
      ) : !form ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>SMTP credentials</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Host" htmlFor="smtp-host">
                  <Input
                    id="smtp-host"
                    value={form.value.host}
                    onChange={(e) => form.patch({ host: e.target.value })}
                    placeholder="smtp.gmail.com"
                  />
                </Field>
                <Field label="Port" htmlFor="smtp-port">
                  <Input
                    id="smtp-port"
                    type="number"
                    value={form.value.port}
                    onChange={(e) => form.patch({ port: e.target.value })}
                    placeholder="587"
                  />
                </Field>
                <Field
                  label="Username (auth)"
                  htmlFor="smtp-username"
                  hint="Usually the same as the From address."
                >
                  <Input
                    id="smtp-username"
                    value={form.value.username}
                    onChange={(e) => form.patch({ username: e.target.value })}
                    placeholder="support@yourdomain.com"
                  />
                </Field>
                <Field
                  label="App password"
                  htmlFor="smtp-password"
                  hint={
                    settingsQuery.data?.has_password
                      ? 'Leave blank to keep the saved password. Type a new one to overwrite, or clear the field then save to remove it.'
                      : 'Google Workspace 16-character App Password.'
                  }
                >
                  <Input
                    id="smtp-password"
                    type="password"
                    value={form.value.password_dirty ? form.value.password : ''}
                    onChange={(e) => form.patch({ password: e.target.value, password_dirty: true })}
                    placeholder={settingsQuery.data?.has_password ? '•••••••••• (saved)' : ''}
                    autoComplete="new-password"
                  />
                </Field>
                <Field label="From email" htmlFor="smtp-from-email">
                  <Input
                    id="smtp-from-email"
                    value={form.value.from_email}
                    onChange={(e) => form.patch({ from_email: e.target.value })}
                    placeholder="support@yourdomain.com"
                  />
                </Field>
                <Field label="From name" htmlFor="smtp-from-name">
                  <Input
                    id="smtp-from-name"
                    value={form.value.from_name}
                    onChange={(e) => form.patch({ from_name: e.target.value })}
                    placeholder="Fasto Support"
                  />
                </Field>
              </div>
              <div className="flex flex-wrap gap-4 pt-2">
                <Checkbox
                  label="Use TLS (StartTLS, port 587)"
                  checked={form.value.use_tls}
                  onCheckedChange={(v) => form.patch({ use_tls: v })}
                />
                <Checkbox
                  label="Use SSL (port 465)"
                  checked={form.value.use_ssl}
                  onCheckedChange={(v) => form.patch({ use_ssl: v })}
                />
                <Checkbox
                  label="Enabled (use this config for outbound CRM email)"
                  checked={form.value.is_enabled}
                  onCheckedChange={(v) => form.patch({ is_enabled: v })}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Test &amp; status</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <Field
                label="Send test to"
                htmlFor="smtp-test-to"
                hint="Sends using the values currently on screen, saved or not."
              >
                <div className="flex gap-2">
                  <Input
                    id="smtp-test-to"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="you@yourdomain.com"
                  />
                  <Button size="sm" variant="outline" onClick={handleTest} disabled={test.isPending}>
                    {test.isPending ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="mr-1 h-3.5 w-3.5" />
                    )}
                    Test
                  </Button>
                </div>
              </Field>

              {settingsQuery.data?.last_test_at ? (
                <div
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs',
                    settingsQuery.data.last_test_status === 'ok'
                      ? 'border-success/30 bg-success/10 text-success'
                      : 'border-danger/30 bg-danger/10 text-danger',
                  )}
                >
                  <div className="flex items-center gap-1.5 font-medium">
                    {settingsQuery.data.last_test_status === 'ok' ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                    Last test {new Date(settingsQuery.data.last_test_at).toLocaleString()}
                  </div>
                  {settingsQuery.data.last_test_error ? (
                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] opacity-80">
                      {settingsQuery.data.last_test_error}
                    </pre>
                  ) : null}
                </div>
              ) : (
                <p className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-muted-foreground">
                  <Mail className="mr-1 inline h-3.5 w-3.5" /> No test runs yet. Use the field above
                  to send a one-off probe before saving.
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      <SaveBar
        visible={!!form?.isDirty}
        summary={pluralizeChanges(form?.changed.length ?? 0)}
        context="to the SMTP configuration"
        isSaving={patch.isPending}
        onSave={() => void handleSave()}
        onDiscard={form?.reset}
      />
    </div>
  );
}
